import { inject, Injectable, OnDestroy } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { from, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, filter, retry, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { trialmanagementexternal } from 'src/shared/services/client/client';
import { ClientService } from 'src/shared/services/client/client.service';
import type { PushNotificationDialogData } from '../components/push-notification-dialog/push-notification-dialog.component';
const PUSH_VAPID_STORAGE_KEY = 'trials_push_vapid_public_key';
export const PUSH_OPTED_OUT_KEY = 'trials_push_opted_out';

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * If the VAPID key changed (or we never stored one), unsubscribe from the current push subscription
 * so the next subscribe() creates a new subscription bound to the current key. Fixes BadJwtToken when
 * backend and frontend keys were rotated but the browser still had a subscription for the old key.
 */
async function unsubscribeIfVapidKeyChanged(vapidPublicKey: string): Promise<void> {
  if (!navigator?.serviceWorker?.ready) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const storedKey = localStorage.getItem(PUSH_VAPID_STORAGE_KEY);
  if (storedKey !== vapidPublicKey) {
    await sub.unsubscribe();
  }
}

/**
 * Registers the trials PWA for push notifications so the API can trigger notifications via SendPushNotification.
 * Call registerIfPossible(userDocId) when the user is logged in (e.g. from app init or home).
 */
/** Payload shape sent by the API (Angular SW format) */
interface PushNotificationPayload {
  notification?: {
    title?: string;
    body?: string;
    data?: {
      onActionClick?: { default?: { url?: string } };
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService implements OnDestroy {
  private swPush = inject(SwPush);
  private clientService = inject(ClientService);
  private messagesSub: Subscription | null = null;

  /** Emits when a push should be shown as an in-app dialog (app in foreground). */
  readonly dialogData$ = new Subject<PushNotificationDialogData>();

  constructor() {
    this.subscribeToPushMessages();
  }

  ngOnDestroy(): void {
    this.messagesSub?.unsubscribe();
  }

  /**
   * When the app is open and a push is received, show the notification in-app as a joii-dialog (e.g. clinician joined video call).
   */
  private subscribeToPushMessages(): void {
    if (!this.swPush.isEnabled) return;
    this.messagesSub = this.swPush.messages
      .pipe(
        filter((msg): msg is object => msg != null && typeof msg === 'object'),
        catchError(() => of(null))
      )
      .subscribe((msg) => {
        if (msg == null) return;
        try {
          const payload: PushNotificationPayload =
            'data' in msg && msg.data != null
              ? typeof msg.data === 'string'
                ? JSON.parse(msg.data)
                : (msg.data as PushNotificationPayload)
              : (msg as PushNotificationPayload);
          const notif = payload.notification;
          if (notif?.title) {
            const url = notif.data?.onActionClick?.default?.url;
            this.dialogData$.next({
              title: notif.title,
              body: notif.body ?? '',
              url,
            });
          }
        } catch {
          // ignore malformed payloads
        }
      });
  }

  /**
   * Subscribes to push (if SW enabled and VAPID key set), then registers the subscription with the API.
   * If the VAPID key changed since last run, unsubscribes first so the new subscription matches the backend (avoids BadJwtToken).
   * Safe to call multiple times; duplicate subscriptions are upserted by endpoint.
   */
  registerIfPossible(userDocId: string) {
    const vapidPublicKey = (environment as { vapidPublicKey?: string }).vapidPublicKey;
    if (!vapidPublicKey || !userDocId) {
      console.warn('[PushNotification] Skipped: vapidPublicKey or userDocId missing', {
        hasVapid: !!vapidPublicKey,
        hasUserDocId: !!userDocId,
      });
      return;
    }
    if (!this.swPush.isEnabled) {
      console.warn(
        '[PushNotification] Skipped: Service worker disabled. On localhost, SW is disabled by config—use staging/prod.'
      );
      return;
    }
    from(
      (async () => {
        await unsubscribeIfVapidKeyChanged(vapidPublicKey);
        // Wait for SW to be ready—fixes Chrome desktop where requestSubscription can hang/fail
        // if called before SW registration completes (e.g. after hard refresh)
        if (navigator.serviceWorker && navigator.serviceWorker.controller == null) {
          await navigator.serviceWorker.ready;
        }
      })()
    )
      .pipe(
        take(1),
        switchMap(() => this.swPush.requestSubscription({ serverPublicKey: vapidPublicKey })),
        // Chrome desktop can fail with "push service error" intermittently; retry once after 2s
        retry({
          count: 1,
          delay: (err) => {
            const msg = typeof err?.message === 'string' ? err.message : '';
            const isPushServiceError = err?.name === 'AbortError' || msg.includes('push service');
            return isPushServiceError ? timer(2000) : throwError(() => err);
          },
        }),
        switchMap((sub) => {
          // Use native toJSON() so keys are base64url-encoded as expected by Web Push
          const raw =
            (
              sub as PushSubscription & {
                toJSON?: () => { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
              }
            ).toJSON?.() ?? null;
          const endpoint = raw?.endpoint ?? sub.endpoint;
          const keys = raw?.keys ?? {
            p256dh: sub.getKey('p256dh') ? arrayBufferToBase64Url(sub.getKey('p256dh')!) : '',
            auth: sub.getKey('auth') ? arrayBufferToBase64Url(sub.getKey('auth')!) : '',
          };
          const payload: trialmanagementexternal.RegisterPushSubscriptionRequest = {
            endpoint,
            keys: {
              p256dh: keys['p256dh'] ?? '',
              auth: keys['auth'] ?? '',
            },
          };
          return this.clientService.client.pipe(
            switchMap((client) => client.trialmanagementexternal.RegisterPushSubscription(userDocId, payload)),
            tap(() => {
              localStorage.setItem(PUSH_VAPID_STORAGE_KEY, vapidPublicKey);
              localStorage.removeItem(PUSH_OPTED_OUT_KEY);
              if (environment.environment === 'localhost') {
                console.log('[PushNotification] Registered successfully');
              }
            })
          );
        }),
        catchError((err) => {
          console.error('[PushNotification] Registration failed:', err);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Unsubscribes from push notifications. Stops receiving pushes; Notification.permission stays
   * granted (browser does not allow revoking it). Call when user opts out via the toggle.
   */
  async unregisterIfPossible(): Promise<void> {
    if (!this.swPush.isEnabled) return;
    try {
      await this.swPush.unsubscribe();
      localStorage.removeItem(PUSH_VAPID_STORAGE_KEY);
      localStorage.setItem(PUSH_OPTED_OUT_KEY, 'true');
    } catch (err) {
      console.error('[PushNotification] Unsubscribe failed:', err);
    }
  }
}
